import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Search, UserCheck, Mail, Phone, Calendar, Users, Crown, UserPlus, RefreshCw, X,
  LayoutList, LayoutGrid, ChevronRight,
  Hash, Clock, Car, CarFront, ArrowUpDown, AlertCircle, TrendingUp, Fuel, Gauge,
  Fingerprint, CalendarDays, CheckCircle2, Lock,
} from "lucide-react";
import moment from "moment";
import Sidebar1 from "components/Sidebar1";
import { fetchWithToken } from "api/ApiHandler";
import { useDialog, DialogFrame, DialogSection, PlateBadge } from "../Admindashboard/ui";
import {
  FONT_HEADING, RED, PAGE_SIZES, formatDate, fromNow, handleAuthError, joinedThisMonth, monthlySignups,
  SignupsChart, Kpi, SideCard, MiniStat, Field, Pagination, Td, EmptyState, RowSkeleton, CardSkeleton,
} from "./shared";


const STATUS_FILTERS = ["All", "Subscribed", "Free"];
const SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  oldest: { label: "Oldest first", fn: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
  name: { label: "Name A–Z", fn: (a, b) => getFullName(a).localeCompare(getFullName(b)) },
};

const getAvatarUrl = (user) => {
  const path = user?.imagePath || user?.image;
  if (!path || path === "image") return null;
  return path;
};

const getInitials = (user) => {
  const first = user?.firstName?.[0] || "";
  const last = user?.lastName?.[0] || "";
  return (first + last).toUpperCase() || "?";
};

const getFullName = (user) => `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Unknown";


const FREE_VEHICLE_LIMIT = 2;

// ─── Mock vehicles ──────────────────────────────────────────────────────────
// The customers API doesn't return vehicles yet, so every customer gets sample
// vehicles (same shape as the app's CreateVechile payload). Set this to false
// once the API sends `vehicles` — real data always wins when it's present.
const USE_MOCK_VEHICLES = true;

const MOCK_VEHICLE_POOL = [
  { vehicleType: "Sedan", vehicleYear: 2021, vehicleMake: "Toyota", vehicleModel: "Camry", vehicleIdentificaionNumber: "4T1B11HK5MU123456", fuelTypeName: "Special 95", currentOdometerReading: 42300, plateSource: "Dubai", plateCategory: "Private", plateCode: "A", plate: "48213" },
  { vehicleType: "SUV", vehicleYear: 2019, vehicleMake: "Nissan", vehicleModel: "Patrol", vehicleIdentificaionNumber: "JN8AY2NY9K9012345", fuelTypeName: "Super 98", currentOdometerReading: 88900, plateSource: "Abu Dhabi", plateCategory: "Private", plateCode: "12", plate: "77410" },
  { vehicleType: "Sedan", vehicleYear: 2022, vehicleMake: "Honda", vehicleModel: "Civic", vehicleIdentificaionNumber: "2HGFE2F59NH543210", fuelTypeName: "Special 95", currentOdometerReading: 18450, plateSource: "Sharjah", plateCategory: "Private", plateCode: "3", plate: "20981" },
  { vehicleType: "Truck", vehicleYear: 2020, vehicleMake: "Ford", vehicleModel: "Ranger", vehicleIdentificaionNumber: "MNCUMFF80LW987654", fuelTypeName: "Diesel", currentOdometerReading: 101200, plateSource: "Dubai", plateCategory: "Commercial", plateCode: "K", plate: "90312" },
  { vehicleType: "Crossover", vehicleYear: 2023, vehicleMake: "Kia", vehicleModel: "Sportage", vehicleIdentificaionNumber: "KNDPU3AF5P7112233", fuelTypeName: "E-Plus 91", currentOdometerReading: 9800, plateSource: "Dubai", plateCategory: "Private", plateCode: "P", plate: "55120" },
  { vehicleType: "SUV", vehicleYear: 2022, vehicleMake: "Hyundai", vehicleModel: "Tucson", vehicleIdentificaionNumber: "KM8JBCAE4NU445566", fuelTypeName: "Special 95", currentOdometerReading: 35600, plateSource: "Dubai", plateCategory: "Private", plateCode: "M", plate: "70834" },
  { vehicleType: "Hatchback", vehicleYear: 2018, vehicleMake: "Volkswagen", vehicleModel: "Golf", vehicleIdentificaionNumber: "", fuelTypeName: "Super 98", currentOdometerReading: 76400, plateSource: "Ajman", plateCategory: "Private", plateCode: "B", plate: "11873" },
  { vehicleType: "Coupe", vehicleYear: 2021, vehicleMake: "BMW", vehicleModel: "430i", vehicleIdentificaionNumber: "WBA53AP00MCF12345", fuelTypeName: "Super 98", currentOdometerReading: 22100, plateSource: "Dubai", plateCategory: "Private", plateCode: "Z", plate: "3310" },
  { vehicleType: "Minivan", vehicleYear: 2020, vehicleMake: "Toyota", vehicleModel: "Innova", vehicleIdentificaionNumber: "", fuelTypeName: "Special 95", currentOdometerReading: 120500, plateSource: "Ras Al Khaimah", plateCategory: "Private", plateCode: "C", plate: "60218" },
];

// Stable per customer: the same customer always gets the same mock vehicles.
const hashId = (id = "") => [...String(id)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

const mockVehiclesFor = (user) => {
  const h = hashId(user?._id || user?.email);
  // Free customers: 1–2 vehicles (the free limit). Subscribers: 3–4.
  const count = user?.subscribed ? 3 + (h % 2) : 1 + (h % 2);
  const selectedIndex = h % count;
  return Array.from({ length: count }, (_, i) => ({
    ...MOCK_VEHICLE_POOL[(h + i * 3) % MOCK_VEHICLE_POOL.length],
    _id: `mock-${user?._id}-${i}`,
    isSelected: i === selectedIndex,
    isMock: true,
  }));
};

// Vehicles come from the customers API. Field names follow the app's
// CreateVechile payload (vehicleMake, vehicleModel, vehicleYear, plate…).
// Returns null when the API didn't include vehicles for this customer.
const getVehicles = (user) => {
  const list = user?.vehicles || user?.vehicle || user?.cars;
  if (Array.isArray(list)) return list;
  const sel = user?.selectedVehicle;
  if (sel && typeof sel === "object") return [sel];
  return USE_MOCK_VEHICLES ? mockVehiclesFor(user) : null;
};

// The vehicle the customer currently has selected in the app. Supports either a
// flag on the vehicle or a reference (id or object) on the customer.
const getSelectedVehicleId = (user) => {
  const ref = user?.selectedVehicle ?? user?.selectedVehicleId ?? user?.activeVehicle ?? user?.defaultVehicle;
  return ref && typeof ref === "object" ? ref._id : ref || null;
};
const isSelectedVehicle = (user, v) =>
  Boolean(v?.isSelected || v?.selected || v?.isDefault || v?.isActive) ||
  (getSelectedVehicleId(user) != null && v?._id === getSelectedVehicleId(user));

const vehicleName = (v) => [v?.vehicleMake, v?.vehicleModel, v?.vehicleYear].filter(Boolean).join(" ") || "Vehicle";
// The app stores fuelType as a sourceId; prefer a name if the API sends one.
const fuelName = (v) => v?.fuelTypeName || v?.fuelName || v?.fuelType?.name || (typeof v?.fuelType === "string" ? v.fuelType : "");


export default function UserManagement() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortKey, setSortKey] = useState("newest");
  const [view, setView] = useState("list");
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const closeDialog = useCallback(() => setSelectedUser(null), []);

  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      setLoadError("");
      const data = await fetchWithToken("/admin/get-all-customers");
      setCustomers(Array.isArray(data?.customers) ? data.customers : []);
    } catch (error) {
      console.error("Fetch customers error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setLoadError("Unable to load customers.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortKey, pageSize]);

  const stats = useMemo(() => {
    const subscribed = customers.filter((c) => c.subscribed).length;
    return {
      total: customers.length,
      subscribed,
      free: customers.length - subscribed,
      rate: customers.length ? Math.round((subscribed / customers.length) * 1000) / 10 : 0,
      ...joinedThisMonth(customers),
    };
  }, [customers]);

  const signupsByMonth = useMemo(() => monthlySignups(customers), [customers]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return customers
      .filter((user) => {
        const matchesSearch =
          !term ||
          getFullName(user).toLowerCase().includes(term) ||
          (user.email || "").toLowerCase().includes(term) ||
          String(user.phNumber || "").toLowerCase().includes(term);
        const matchesStatus =
          statusFilter === "All" ||
          (statusFilter === "Subscribed" && user.subscribed) ||
          (statusFilter === "Free" && !user.subscribed);
        return matchesSearch && matchesStatus;
      })
      .sort(SORTS[sortKey].fn);
  }, [customers, searchTerm, statusFilter, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const currentItems = filteredUsers.slice(pageStart, pageStart + pageSize);

  const recentCustomers = useMemo(
    () => [...customers].sort(SORTS.newest.fn).slice(0, 5),
    [customers]
  );

  const statusCount = { All: stats.total, Subscribed: stats.subscribed, Free: stats.free };

  return (
    <div className="flex h-screen bg-[#F6F6F8] overflow-hidden">
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      <main className="flex-1 min-w-0 overflow-y-auto px-8 py-6 lg:px-6 md:px-4 sm:px-3">
        {/* Header */}
        <header className="flex items-center justify-between gap-4 mb-6 sm:flex-col sm:items-start">
          <div>
            <h1 className="m-0 text-3xl sm:text-2xl font-medium text-[#111]" style={FONT_HEADING}>Customers</h1>
            <p className="m-0 mt-1 text-sm text-[#6B7280]">Everyone who signed up on the Fuelingo app, their subscription and account details.</p>
          </div>
          <button
            type="button"
            onClick={loadCustomers}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-[#fff] border border-[#ECECEF] px-4 py-2.5 text-sm font-medium text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B] transition disabled:opacity-60"
          >
            <RefreshCw size={15} className={isLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </header>

        {/* KPIs */}
        <section className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-5 md:gap-4 mb-6">
          <Kpi Icon={Users} label="Total customers" value={stats.total} loading={isLoading} />
          <Kpi Icon={Crown} label="Subscribed" value={stats.subscribed} sub={`${stats.rate}% of customers`} loading={isLoading} />
          <Kpi Icon={UserCheck} label="Free plan" value={stats.free} sub="Up to 2 vehicles" loading={isLoading} />
          <Kpi Icon={UserPlus} label="Joined this month" value={stats.thisMonth} change={stats.monthChange} loading={isLoading} />
        </section>

        <div className="grid grid-cols-[minmax(0,1fr)_340px] lg:grid-cols-[minmax(0,1fr)_300px] md:grid-cols-1 gap-6 md:gap-4">
          {/* List card */}
          <section className="min-w-0 bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 md:p-5 sm:p-4 self-start" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
            {/* Toolbar */}
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <div className="relative flex-1 min-w-[220px] sm:min-w-0 sm:w-full">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
                {/* No `type` attr on purpose: styles/tailwind.css resets padding/border on
                    [type="search"] with higher priority than these utility classes. */}
                <input
                  role="searchbox"
                  enterKeyHint="search"
                  onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email or phone"
                  className="w-full pl-10 pr-9 py-2.5 bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl text-sm text-[#111] placeholder-[#9CA3AF] focus:outline-none focus:border-[#EA352B] focus:bg-[#fff] focus:ring-0 transition"
                />
                {searchTerm && (
                  <button type="button" aria-label="Clear search" onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111]">
                    <X size={15} />
                  </button>
                )}
              </div>

              <label className="relative inline-flex items-center sm:flex-1">
                <ArrowUpDown size={14} className="absolute left-3 text-[#9CA3AF] pointer-events-none" />
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  aria-label="Sort customers"
                  className="w-full appearance-none bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl pl-8 pr-8 py-2.5 text-sm text-[#374151] focus:outline-none focus:border-[#EA352B] focus:ring-0 cursor-pointer"
                >
                  {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                </select>
              </label>

              <div className="inline-flex rounded-xl bg-[#F3F3F6] p-1" role="group" aria-label="View">
                {[["list", LayoutList], ["grid", LayoutGrid]].map(([v, Icon]) => (
                  <button
                    key={v}
                    type="button"
                    aria-label={`${v} view`}
                    aria-pressed={view === v}
                    onClick={() => setView(v)}
                    className={`w-9 h-8 rounded-lg flex items-center justify-center transition ${view === v ? "bg-[#fff] text-[#EA352B] shadow-sm" : "text-[#9CA3AF] hover:text-[#111]"}`}
                  >
                    <Icon size={16} />
                  </button>
                ))}
              </div>
            </div>

            {/* Status tabs */}
            <div className="flex gap-2 flex-wrap mb-5">
              {STATUS_FILTERS.map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatusFilter(st)}
                  className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                    statusFilter === st ? "bg-[#EA352B] text-[#fff] shadow-sm" : "bg-[#F3F3F6] text-[#6B7280] hover:text-[#111]"
                  }`}
                >
                  {st}
                  <span className={`rounded-full px-1.5 py-px text-[10px] ${statusFilter === st ? "bg-[#ffffff33]" : "bg-[#fff]"}`}>
                    {isLoading ? "…" : statusCount[st]}
                  </span>
                </button>
              ))}
            </div>

            {/* Content */}
            {loadError ? (
              <EmptyState
                Icon={AlertCircle}
                title={loadError}
                text="Check your connection and try again."
                action={<button type="button" onClick={loadCustomers} className="mt-3 rounded-xl bg-[#EA352B] text-[#fff] px-4 py-2 text-sm font-medium">Try again</button>}
              />
            ) : !isLoading && currentItems.length === 0 ? (
              <EmptyState
                Icon={Search}
                title="No customers found"
                text={searchTerm || statusFilter !== "All" ? "Try a different search or filter." : "Customers will appear here once they sign up."}
              />
            ) : view === "list" ? (
              <>
                {/* Table (tablet & desktop) */}
                <div className="sm:hidden overflow-x-auto -mx-6 md:-mx-5 px-6 md:px-5">
                  <table className="w-full min-w-[760px] text-left border-separate border-spacing-0">
                    <thead>
                      <tr>
                        {["Customer", "Phone", "Vehicles", "Joined", "Subscription", ""].map((h, i) => (
                          <th key={i} className="py-3 px-3 first:pl-0 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF] border-b border-[#EEEEF1] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading
                        ? Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)
                        : currentItems.map((user) => (
                          <tr
                            key={user._id}
                            tabIndex={0}
                            role="button"
                            aria-label={`Open ${getFullName(user)}`}
                            onClick={() => setSelectedUser(user)}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setSelectedUser(user))}
                            className="group cursor-pointer hover:bg-[#FFF7F6] focus:outline-none focus-visible:bg-[#FFF7F6] transition-colors"
                          >
                            <Td>
                              <div className="flex items-center gap-3 min-w-0">
                                <UserAvatar user={user} />
                                <div className="min-w-0">
                                  <p className="m-0 text-sm font-medium text-[#111] truncate group-hover:text-[#EA352B] transition-colors">{getFullName(user)}</p>
                                  <p className="m-0 text-[12px] text-[#9CA3AF] truncate">{user.email || "No email"}</p>
                                </div>
                              </div>
                            </Td>
                            <Td>{user.phNumber || <span className="text-[#C0C4CC]">—</span>}</Td>
                            <Td><VehiclesCell user={user} /></Td>
                            <Td>
                              <p className="m-0 text-sm text-[#374151]">{formatDate(user.createdAt)}</p>
                              <p className="m-0 text-[11px] text-[#9CA3AF]">{fromNow(user.createdAt)}</p>
                            </Td>
                            <Td><SubscriptionBadge subscribed={user.subscribed} /></Td>
                            <Td className="w-8 text-right">
                              <ChevronRight size={16} className="text-[#C0C4CC] group-hover:text-[#EA352B] group-hover:translate-x-0.5 transition" />
                            </Td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Stacked list (phones) */}
                <ul className="hidden sm:block m-0 pl-0 list-none divide-y divide-[#F1F1F4]">
                  {isLoading
                    ? Array.from({ length: 6 }).map((_, i) => <li key={i} className="py-3"><CardSkeleton compact /></li>)
                    : currentItems.map((user) => (
                      <li key={user._id}>
                        <button type="button" onClick={() => setSelectedUser(user)} className="w-full text-left flex items-center gap-3 py-3">
                          <UserAvatar user={user} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-[#111] truncate">{getFullName(user)}</span>
                            <span className="block text-[11px] text-[#9CA3AF] truncate">Joined {formatDate(user.createdAt)}</span>
                          </span>
                          <SubscriptionBadge subscribed={user.subscribed} compact />
                        </button>
                      </li>
                    ))}
                </ul>
              </>
            ) : (
              <div className="grid grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-4">
                {isLoading
                  ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
                  : currentItems.map((user) => (
                    <button
                      key={user._id}
                      type="button"
                      onClick={() => setSelectedUser(user)}
                      className="group text-left rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] p-4 hover:bg-[#fff] hover:border-[#F6C9C5] hover:shadow-[0_8px_24px_rgba(234,53,43,0.08)] hover:-translate-y-0.5 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <UserAvatar user={user} size={44} />
                        <div className="min-w-0">
                          <p className="m-0 text-sm font-medium text-[#111] truncate group-hover:text-[#EA352B] transition-colors">{getFullName(user)}</p>
                          <p className="m-0 text-[11px] text-[#9CA3AF] truncate">{user.email || "No email"}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#EEEEF1]">
                        <span className="text-[11px] text-[#6B7280] inline-flex items-center gap-1"><Calendar size={12} /> {formatDate(user.createdAt)}</span>
                        <SubscriptionBadge subscribed={user.subscribed} compact />
                      </div>
                    </button>
                  ))}
              </div>
            )}

            {/* Pagination */}
            {!isLoading && !loadError && filteredUsers.length > 0 && (
              <div className="flex items-center justify-between gap-3 flex-wrap mt-6 pt-4 border-t border-[#F1F1F4]">
                <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                  <span>
                    Showing <b className="text-[#111]">{pageStart + 1}–{Math.min(pageStart + pageSize, filteredUsers.length)}</b> of{" "}
                    <b className="text-[#111]">{filteredUsers.length}</b>
                  </span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    aria-label="Rows per page"
                    className="bg-[#F7F7F9] border border-[#EEEEF1] rounded-lg py-1 pl-2 pr-7 text-xs focus:outline-none focus:ring-0 focus:border-[#EA352B]"
                  >
                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                </div>
                {totalPages > 1 && <Pagination page={currentPage} total={totalPages} onChange={setCurrentPage} />}
              </div>
            )}
          </section>

          {/* Side column */}
          <aside className="min-w-0 space-y-6 md:space-y-0 md:grid md:grid-cols-2 md:gap-4 sm:grid-cols-1">
            <SideCard title="Subscription split" Icon={Crown}>
              {isLoading ? (
                <div className="h-24 rounded-xl bg-[#F3F3F6] animate-pulse" />
              ) : (
                <>
                  <div className="flex items-end justify-between">
                    <span className="text-3xl font-semibold text-[#111]">{stats.rate}%</span>
                    <span className="text-xs text-[#6B7280]">subscribed</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#F1F1F4] overflow-hidden mt-3 flex">
                    <div className="h-full bg-[#EA352B] transition-all duration-700" style={{ width: `${stats.rate}%` }} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <MiniStat color={RED} label="Subscribed" value={stats.subscribed} />
                    <MiniStat color="#D1D5DB" label="Free" value={stats.free} />
                  </div>
                </>
              )}
            </SideCard>

            <SideCard title="Signups · last 6 months" Icon={TrendingUp}>
              <SignupsChart data={signupsByMonth} loading={isLoading} />
            </SideCard>

            <SideCard title="Recently joined" Icon={UserPlus}>
              <div className="space-y-2">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} compact />)
                ) : recentCustomers.length === 0 ? (
                  <p className="m-0 text-sm text-[#9CA3AF] text-center py-6">No customers yet.</p>
                ) : (
                  recentCustomers.map((c) => (
                    <button
                      key={c._id}
                      type="button"
                      onClick={() => setSelectedUser(c)}
                      className="w-full text-left flex items-center gap-3 rounded-2xl p-2.5 hover:bg-[#F7F7F9] transition"
                    >
                      <UserAvatar user={c} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-[#111] truncate">{getFullName(c)}</span>
                        <span className="block text-[11px] text-[#9CA3AF]">{fromNow(c.createdAt)}</span>
                      </span>
                      <SubscriptionBadge subscribed={c.subscribed} compact />
                    </button>
                  ))
                )}
              </div>
            </SideCard>
          </aside>
        </div>
      </main>

      <CustomerDialog user={selectedUser} onClose={closeDialog} />
    </div>
  );
}

// ─── Customer details dialog ────────────────────────────────────────────────

function CustomerDialog({ user, onClose }) {
  const closeRef = useRef(null);
  useDialog(!!user, onClose);
  useEffect(() => {
    if (user) closeRef.current?.focus();
  }, [user]);

  if (!user) return null;

  const vehicles = getVehicles(user);
  const joined = moment.utc(user.createdAt);
  const memberFor = joined.isValid() ? joined.fromNow(true) : "-";

  return (
    <DialogFrame labelledBy="customer-dialog-title" onClose={onClose} maxWidth={760}>
      <div
        className="relative shrink-0 px-6 pt-6 pb-6 sm:px-4 text-[#fff] overflow-hidden"
        style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 60%, #EA352B 140%)" }}
      >
        <div className="absolute -right-12 -top-16 w-52 h-52 rounded-full bg-[#EA352B] opacity-20" />
        <button
          ref={closeRef}
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute z-10 top-4 right-4 w-9 h-9 rounded-xl bg-[#ffffff1f] hover:bg-[#ffffff33] flex items-center justify-center transition"
        >
          <X size={18} className="text-[#fff]" />
        </button>
        <div className="relative flex items-center gap-4 sm:flex-col sm:items-start pr-10">
          <UserAvatar user={user} size={76} ring />
          <div className="min-w-0">
            <SubscriptionBadge subscribed={user.subscribed} />
            <h2 id="customer-dialog-title" className="m-0 mt-2 text-2xl sm:text-xl font-medium truncate" style={FONT_HEADING}>
              {getFullName(user)}
            </h2>
            <p className="m-0 mt-1 text-xs text-[#ffffffb3] inline-flex items-center gap-1.5">
              <Clock size={12} /> Customer for {memberFor}
            </p>
          </div>
        </div>
        <div className="relative flex gap-2 mt-5 flex-wrap">
          {user.email && (
            <a href={`mailto:${user.email}`} className="no-underline inline-flex items-center gap-1.5 rounded-xl bg-[#fff] text-[#111] px-3.5 py-2 text-xs font-semibold hover:bg-[#FDECEA] transition">
              <Mail size={14} className="text-[#EA352B]" /> Send email
            </a>
          )}
          {user.phNumber && (
            <a href={`tel:${user.phNumber}`} className="no-underline inline-flex items-center gap-1.5 rounded-xl bg-[#ffffff1f] text-[#fff] px-3.5 py-2 text-xs font-semibold hover:bg-[#ffffff33] transition">
              <Phone size={14} /> Call
            </a>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-4 grid grid-cols-2 sm:grid-cols-1 gap-4">
        <DialogSection title="Contact">
          <div className="space-y-2">
            <Field Icon={Mail} label="Email" value={user.email} copy />
            <Field Icon={Phone} label="Phone" value={user.phNumber} copy />
          </div>
        </DialogSection>

        <DialogSection title="Account">
          <div className="space-y-2">
            <Field Icon={Calendar} label="Joined" value={formatDate(user.createdAt, true)} />
            {user.updatedAt && <Field Icon={Clock} label="Last updated" value={`${formatDate(user.updatedAt, true)} · ${fromNow(user.updatedAt)}`} />}
            <Field Icon={Hash} label="Customer ID" value={user._id} copy mono />
          </div>
        </DialogSection>

        <div className="col-span-2 sm:col-span-1">
          <DialogSection title="Subscription">
            <div className={`flex items-center gap-4 rounded-2xl p-4 ${user.subscribed ? "bg-[#FFF7F6] border border-[#F6C9C5]" : "bg-[#F7F7F9] border border-[#EEEEF1]"}`}>
              <span className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${user.subscribed ? "bg-[#EA352B]" : "bg-[#E5E7EB]"}`}>
                <Crown size={22} className={user.subscribed ? "text-[#fff]" : "text-[#9CA3AF]"} />
              </span>
              <div className="min-w-0">
                <p className="m-0 text-sm font-semibold text-[#111]">{user.subscribed ? "Premium subscriber" : "Free plan"}</p>
                <p className="m-0 mt-0.5 text-xs text-[#6B7280]">
                  {user.subscribed
                    ? "Can add unlimited vehicles and gets exclusive perks & discounts."
                    : "Can add up to 2 vehicles. Adding a 3rd vehicle requires a subscription."}
                </p>
              </div>
            </div>
          </DialogSection>
        </div>

        <div className="col-span-2 sm:col-span-1">
          <VehiclesSection user={user} vehicles={vehicles} />
        </div>
      </div>
    </DialogFrame>
  );
}

// ─── Vehicles ───────────────────────────────────────────────────────────────

function VehiclesCell({ user }) {
  const vehicles = getVehicles(user);
  if (!vehicles) return <span className="text-[#C0C4CC]">—</span>;
  if (vehicles.length === 0) return <span className="text-xs text-[#9CA3AF]">None added</span>;
  const selected = vehicles.find((v) => isSelectedVehicle(user, v));
  return (
    <div className="min-w-0">
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#111]">
        <Car size={14} className="text-[#EA352B]" /> {vehicles.length}
        {!user.subscribed && <span className="text-[11px] font-normal text-[#9CA3AF]">/ {FREE_VEHICLE_LIMIT}</span>}
      </span>
      {selected && (
        <span className="block text-[11px] text-[#9CA3AF] truncate max-w-[160px]">
          <CheckCircle2 size={10} className="inline -mt-px mr-0.5 text-[#16A34A]" />
          {vehicleName(selected)}
        </span>
      )}
    </div>
  );
}

function VehiclesSection({ user, vehicles }) {
  if (!vehicles) {
    return (
      <DialogSection title="Vehicles">
        <div className="flex items-center gap-3 rounded-2xl bg-[#F7F7F9] p-4">
          <Car size={20} className="text-[#C0C4CC] shrink-0" />
          <p className="m-0 text-sm text-[#6B7280]">Vehicle details aren’t available for this customer yet.</p>
        </div>
      </DialogSection>
    );
  }

  // Selected vehicle first, then the rest
  const sorted = [...vehicles].sort((a, b) => isSelectedVehicle(user, b) - isSelectedVehicle(user, a));
  const selected = sorted.find((v) => isSelectedVehicle(user, v));
  const others = sorted.filter((v) => v !== selected);
  const atLimit = !user.subscribed && vehicles.length >= FREE_VEHICLE_LIMIT;

  return (
    <DialogSection
      title={
        <span className="inline-flex items-center gap-2">
          Vehicles · {vehicles.length}
          {vehicles.some((v) => v.isMock) && (
            <span className="normal-case tracking-normal rounded-full bg-[#FEF3C7] text-[#B45309] px-2 py-0.5 text-[10px] font-semibold">Sample data</span>
          )}
        </span>
      }
      action={
        user.subscribed ? (
          <span className="text-[11px] font-semibold text-[#EA352B]">Unlimited</span>
        ) : (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${atLimit ? "text-[#B45309]" : "text-[#6B7280]"}`}>
            {atLimit && <Lock size={11} />} {Math.min(vehicles.length, FREE_VEHICLE_LIMIT)} / {FREE_VEHICLE_LIMIT} free slots used
          </span>
        )
      }
    >
      {vehicles.length === 0 ? (
        <p className="m-0 text-sm text-[#9CA3AF]">This customer hasn’t added a vehicle yet.</p>
      ) : (
        <div className="space-y-4">
          {selected ? (
            <VehicleCard vehicle={selected} selected />
          ) : (
            <p className="m-0 text-xs text-[#9CA3AF]">No vehicle is currently selected in the app.</p>
          )}
          {others.length > 0 && (
            <div>
              <p className="m-0 mb-2 text-[11px] font-semibold text-[#6B7280]">
                {selected ? "Other vehicles" : "All vehicles"} · {others.length}
              </p>
              <div className="space-y-2">
                {others.map((v, i) => <VehicleCard key={v._id || i} vehicle={v} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </DialogSection>
  );
}

function VehicleCard({ vehicle: v, selected }) {
  const [open, setOpen] = useState(selected);
  const odometer = v.currentOdometerReading != null && v.currentOdometerReading !== ""
    ? `${Number(v.currentOdometerReading).toLocaleString()} km`
    : "";
  const hasPlate = v.plate || v.plateCode || v.plateSource;

  return (
    <div className={`rounded-2xl border overflow-hidden ${selected ? "border-[#EA352B] bg-[#FFF7F6] shadow-[0_6px_20px_rgba(234,53,43,0.10)]" : "border-[#EEEEF1] bg-[#fff]"}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full text-left flex items-center gap-3 p-3"
      >
        <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${selected ? "bg-[#EA352B]" : "bg-[#1B1B22]"}`}>
          <CarFront size={20} className="text-[#fff]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[#111] truncate">{vehicleName(v)}</span>
            {selected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EA352B] text-[#fff] px-2 py-0.5 text-[10px] font-semibold">
                <CheckCircle2 size={10} /> Selected
              </span>
            )}
          </span>
          <span className="block text-[11px] text-[#9CA3AF] truncate">
            {[v.vehicleType, [v.plateSource, v.plateCode, v.plate].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "No extra details"}
          </span>
        </span>
        <ChevronRight size={16} className={`text-[#9CA3AF] shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="grid grid-cols-3 sm:grid-cols-2 gap-2">
            <Spec Icon={CarFront} label="Type" value={v.vehicleType} />
            <Spec Icon={CalendarDays} label="Year" value={v.vehicleYear} />
            <Spec Icon={Fuel} label="Fuel" value={fuelName(v)} />
            <Spec Icon={Gauge} label="Odometer" value={odometer} />
            <Spec Icon={Car} label="Make" value={v.vehicleMake} />
            <Spec Icon={Car} label="Model" value={v.vehicleModel} />
          </div>
          <Spec Icon={Fingerprint} label="VIN" value={v.vehicleIdentificaionNumber} mono />
          {hasPlate && <PlateBadge car={v} />}
        </div>
      )}
    </div>
  );
}

function Spec({ Icon, label, value, mono }) {
  return (
    <div className="rounded-xl bg-[#F7F7F9] px-3 py-2 min-w-0">
      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#9CA3AF]">
        <Icon size={11} className="shrink-0" /> {label}
      </span>
      <span className={`block mt-0.5 text-[13px] font-medium truncate ${value ? "text-[#111]" : "text-[#C0C4CC]"} ${mono ? "font-mono tracking-wide" : ""}`}>
        {value || "Not provided"}
      </span>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function UserAvatar({ user, size = 40, ring }) {
  const url = getAvatarUrl(user);
  const fontSize = Math.max(10, Math.round(size * 0.36));
  if (url) {
    return (
      <img
        src={url}
        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
        alt=""
        className={`rounded-xl object-cover shrink-0 bg-[#F3F3F6] ${ring ? "border-4 border-[#ffffff33]" : ""}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className={`rounded-xl shrink-0 bg-[#EA352B] flex items-center justify-center font-semibold text-[#fff] select-none ${ring ? "border-4 border-[#ffffff33]" : ""}`}
      style={{ width: size, height: size, fontSize }}
    >
      {getInitials(user)}
    </span>
  );
}

function SubscriptionBadge({ subscribed, compact }) {
  return subscribed ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#FDECEA] text-[#EA352B] px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap">
      <Crown size={11} /> {compact ? "Pro" : "Subscribed"}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-[#F3F4F6] text-[#6B7280] px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap">
      Free
    </span>
  );
}










